import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FiCamera, FiCheck, FiUploadCloud } from 'react-icons/fi';
import { RxCross2 } from 'react-icons/rx';
import { useTranslation } from 'react-i18next';
import { Bars } from 'react-loader-spinner';

import { MainCustomButton, SecondaryCustomButton } from '../../../../../components/Buttons';
import { PassportCamera } from './usePassportCamera';
import { canvasFromBlob, createMrzOcrWorker, MRZ_FRAME, readMrzFromCanvas } from './readMrzFromImage';
import { mapMrzParseToForm, MrzFormFields } from './mapMrzToForm';

const ATTEMPTS_BEFORE_TIP = 6;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type PassportMrzScanModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  primaryColor: string;
  camera: PassportCamera;
  onSuccess: (fields: MrzFormFields) => void;
};

export const PassportMrzScanModal: React.FC<PassportMrzScanModalProps> = ({
  open,
  onOpenChange,
  primaryColor,
  camera,
  onSuccess
}) => {
  const { t } = useTranslation();
  const { videoRef, status, captureFrame, start } = camera;
  const workerRef = useRef<Awaited<ReturnType<typeof createMrzOcrWorker>> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const readingSessionRef = useRef(0);
  const workerLockRef = useRef(Promise.resolve());
  const openRef = useRef(open);
  const previewRef = useRef<MrzFormFields | null>(null);
  const photoBusyRef = useRef(false);
  const [workerReady, setWorkerReady] = useState(false);
  const [workerFailed, setWorkerFailed] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [ocrNonce, setOcrNonce] = useState(0);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const [preview, setPreview] = useState<MrzFormFields | null>(null);

  openRef.current = open;
  previewRef.current = preview;
  photoBusyRef.current = photoBusy;

  useEffect(() => {
    if (!open) {
      readingSessionRef.current += 1;
      previewRef.current = null;
      setWorkerReady(false);
      setWorkerFailed(false);
      setAttempts(0);
      setPhotoBusy(false);
      setPhotoError(false);
      setPreview(null);
      return;
    }

    let cancelled = false;
    setWorkerReady(false);
    setWorkerFailed(false);
    void createMrzOcrWorker()
      .then((worker) => {
        if (cancelled) {
          void worker.terminate();
          return;
        }
        workerRef.current = worker;
        setWorkerReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setWorkerFailed(true);
        }
      });

    return () => {
      cancelled = true;
      readingSessionRef.current += 1;
      const worker = workerRef.current;
      workerRef.current = null;
      workerLockRef.current = Promise.resolve();
      setWorkerReady(false);
      void worker?.terminate();
    };
  }, [open, ocrNonce]);

  const withWorker = useCallback(async <T,>(session: number, task: (worker: NonNullable<typeof workerRef.current>) => Promise<T>) => {
    const previous = workerLockRef.current;
    let release: () => void = () => undefined;
    workerLockRef.current = new Promise<void>((resolve) => {
      release = () => resolve();
    });
    await previous;
    try {
      const worker = workerRef.current;
      if (!worker || !openRef.current || readingSessionRef.current !== session) {
        throw new Error('WORKER_UNAVAILABLE');
      }
      return await task(worker);
    } finally {
      release();
    }
  }, []);

  const acceptReading = useCallback((fields: MrzFormFields) => {
    previewRef.current = fields;
    photoBusyRef.current = false;
    setPreview(fields);
    setPhotoBusy(false);
  }, []);

  useEffect(() => {
    if (!open || status !== 'live' || !workerReady) {
      return;
    }

    let cancelled = false;
    let attempt = 0;

    const scan = async () => {
      while (!cancelled) {
        if (previewRef.current || photoBusyRef.current) {
          await sleep(250);
          continue;
        }

        const frame = captureFrame();
        if (!frame) {
          await sleep(300);
          continue;
        }

        const session = readingSessionRef.current;
        try {
          const parsed = await withWorker(session, (worker) =>
            readMrzFromCanvas(frame, worker, attempt, { mode: attempt % 4 === 3 ? 'still' : 'live' })
          );
          if (cancelled || !openRef.current) {
            return;
          }
          if (readingSessionRef.current !== session || photoBusyRef.current || previewRef.current) {
            continue;
          }
          acceptReading(mapMrzParseToForm(parsed));
          attempt = 0;
        } catch {
          if (!cancelled && readingSessionRef.current === session) {
            attempt += 1;
            setAttempts(attempt);
          }
          await sleep(200);
        }
      }
    };

    void scan();

    return () => {
      cancelled = true;
    };
  }, [open, status, workerReady, ocrNonce, captureFrame, withWorker, acceptReading]);

  const handleRetry = () => {
    setAttempts(0);
    setPhotoError(false);
    if (workerFailed) {
      setWorkerFailed(false);
      setWorkerReady(false);
      setOcrNonce((value) => value + 1);
    }
    if (status !== 'live') {
      void start();
    }
  };

  const handleScanAgain = () => {
    readingSessionRef.current += 1;
    previewRef.current = null;
    setPreview(null);
    setAttempts(0);
    setPhotoError(false);
  };

  const handleConfirm = () => {
    if (!preview) {
      return;
    }
    onSuccess(preview);
    onOpenChange(false);
  };

  const updatePreview = (key: keyof MrzFormFields, value: string) => {
    setPreview((current) => {
      if (!current) {
        return current;
      }
      const next: MrzFormFields =
        key === 'gender'
          ? { ...current, gender: value === 'male' || value === 'female' ? value : undefined }
          : ({ ...current, [key]: value } as MrzFormFields);
      previewRef.current = next;
      return next;
    });
  };

  const fieldLabel = (key: string) => String(t(key) || '').replace(/\s*\*\s*$/, '');

  const handleCapture = async () => {
    if (!openRef.current || photoBusyRef.current || previewRef.current || !workerReady || workerFailed) {
      return;
    }

    const session = ++readingSessionRef.current;
    photoBusyRef.current = true;
    setPhotoBusy(true);
    setPhotoError(false);

    try {
      const frame = captureFrame();
      if (!frame) {
        throw new Error('NO_FRAME');
      }
      const parsed = await withWorker(session, (worker) => readMrzFromCanvas(frame, worker, 0, { mode: 'still' }));
      if (readingSessionRef.current !== session || !openRef.current) {
        return;
      }
      acceptReading(mapMrzParseToForm(parsed));
    } catch {
      if (readingSessionRef.current === session && openRef.current) {
        photoBusyRef.current = false;
        setPhotoBusy(false);
        setPhotoError(true);
      }
    }
  };

  const handlePhoto = async (file: File | undefined) => {
    if (!file || !openRef.current || photoBusyRef.current || previewRef.current || !workerReady || workerFailed) {
      return;
    }

    if (file.type && !file.type.startsWith('image/')) {
      setPhotoError(true);
      return;
    }

    const session = ++readingSessionRef.current;
    photoBusyRef.current = true;
    setPhotoBusy(true);
    setPhotoError(false);

    try {
      const canvas = await canvasFromBlob(file);
      const parsed = await withWorker(session, (worker) => readMrzFromCanvas(canvas, worker, 0, { mode: 'still' }));
      if (readingSessionRef.current !== session || !openRef.current) {
        return;
      }
      acceptReading(mapMrzParseToForm(parsed));
    } catch {
      if (readingSessionRef.current === session && openRef.current) {
        setPhotoError(true);
      }
    } finally {
      if (readingSessionRef.current === session && openRef.current && !previewRef.current) {
        photoBusyRef.current = false;
        setPhotoBusy(false);
      }
      if (readingSessionRef.current === session && fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const cameraBlocked = status === 'denied' || status === 'unavailable';
  const blocked = cameraBlocked || workerFailed;
  const preparing = !blocked && !photoBusy && (status === 'requesting' || !workerReady);
  const showVideo = open && !cameraBlocked;
  const canUpload = workerReady && !photoBusy && !workerFailed && !preview;

  const blockedMessage = workerFailed
    ? t('labels.lblScanPassportFailed')
    : status === 'denied'
      ? t('labels.lblScanPassportDenied')
      : t('labels.lblScanPassportUnavailable');

  const liveMessage = photoBusy
    ? t('labels.lblScanPassportCapturing')
    : status === 'requesting'
      ? t('labels.lblScanPassportPermission')
      : !workerReady
        ? t('labels.lblScanPassportPreparing')
        : t('labels.lblScanPassportSearching');

  const overlayStyle = {
    left: `${MRZ_FRAME.insetX * 100}%`,
    right: `${MRZ_FRAME.insetX * 100}%`,
    bottom: `${MRZ_FRAME.insetBottom * 100}%`,
    height: `${MRZ_FRAME.height * 100}%`
  };

  const inputClassName = 'h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-400';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-overlayShow" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[90vw] max-w-[560px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl focus:outline-none data-[state=open]:animate-contentShow">
          <Dialog.Close asChild>
            <button
              type="button"
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('actions.close') || 'Close'}
            >
              <RxCross2 size={18} />
            </button>
          </Dialog.Close>

          <Dialog.Title className="pr-8 text-lg font-semibold" style={{ color: primaryColor }}>
            {preview ? t('labels.lblScanPassportFound') : t('labels.lblScanPassport')}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-slate-600">
            {preview ? t('labels.lblScanPassportReviewHint') : t('labels.lblScanPassportHint')}
          </Dialog.Description>

          {preview && (
            <div className="mt-4">
              <div className="mb-4 flex items-center gap-3 rounded-xl bg-emerald-50 px-3 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <FiCheck size={18} />
                </span>
                <p className="text-sm font-medium text-emerald-800">{t('labels.lblScanPassportFound')}</p>
              </div>

              <div className="grid gap-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-500">{fieldLabel('labels.lblFirstName')}</span>
                  <input
                    value={preview.name || ''}
                    onChange={(event) => updatePreview('name', event.target.value)}
                    className={inputClassName}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-500">{fieldLabel('labels.lblPassportNo')}</span>
                  <input
                    value={preview.passportNo || ''}
                    onChange={(event) => updatePreview('passportNo', event.target.value)}
                    className={inputClassName}
                  />
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-500">{t('labels.lblBirthDate')}</span>
                    <input
                      value={preview.dateOfBirth || ''}
                      onChange={(event) => updatePreview('dateOfBirth', event.target.value)}
                      className={inputClassName}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-500">{fieldLabel('labels.lblExpiryDate')}</span>
                    <input
                      value={preview.expiryDate || ''}
                      onChange={(event) => updatePreview('expiryDate', event.target.value)}
                      className={inputClassName}
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-500">{fieldLabel('labels.lblGender')}</span>
                  <select
                    value={preview.gender || ''}
                    onChange={(event) => updatePreview('gender', event.target.value)}
                    className={`${inputClassName} bg-white`}
                  >
                    <option value="">{t('labels.lblSelect')}</option>
                    <option value="male">{t('labels.lblGenderMale')}</option>
                    <option value="female">{t('labels.lblGenderFemale')}</option>
                  </select>
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-500">{fieldLabel('labels.lblNationality')}</span>
                    <input
                      value={preview.nationality || ''}
                      onChange={(event) => updatePreview('nationality', event.target.value)}
                      className={inputClassName}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-500">
                      {fieldLabel('labels.lblPassportIssuingCountry')}
                    </span>
                    <input
                      value={preview.passportIssueCountry || ''}
                      onChange={(event) => updatePreview('passportIssueCountry', event.target.value)}
                      className={inputClassName}
                    />
                  </label>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleScanAgain}
                  className="h-10 rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {t('labels.lblScanPassportScanAgain')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="h-10 rounded-lg px-4 text-sm font-semibold text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  {t('labels.lblScanPassportUseData')}
                </button>
              </div>
            </div>
          )}

          <div className={preview ? 'hidden' : undefined}>
            <div className="relative mt-4 overflow-hidden rounded-xl bg-slate-900">
              {showVideo ? (
                <>
                  <video ref={videoRef} className="h-[320px] w-full object-cover object-center" playsInline muted autoPlay />
                  <div
                    className="pointer-events-none absolute rounded-md border-2 border-dashed border-white/90"
                    style={overlayStyle}
                  >
                    <span className="absolute -top-6 left-0 rounded bg-black/60 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                      MRZ
                    </span>
                  </div>
                  <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-center gap-2 bg-gradient-to-b from-black/70 to-transparent px-4 py-3">
                    {preparing || photoBusy ? (
                      <Bars height="16" width="16" color="#ffffff" visible />
                    ) : (
                      <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                    )}
                    <p className="text-sm text-white">{liveMessage}</p>
                  </div>
                  {attempts >= ATTEMPTS_BEFORE_TIP && !photoBusy && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8">
                      <p className="text-center text-xs text-white">{t('labels.lblScanPassportTip')}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex h-[220px] flex-col items-center justify-center px-6 text-center">
                  {photoBusy ? (
                    <>
                      <Bars height="28" width="28" color="#ffffff" visible />
                      <p className="mt-3 text-sm text-white">{t('labels.lblScanPassportReadingPhoto')}</p>
                    </>
                  ) : (
                    <>
                      <FiCamera className="mb-3 text-white" size={32} />
                      <p className="text-sm text-white">{blockedMessage}</p>
                    </>
                  )}
                </div>
              )}
            </div>

            {photoError && (
              <p className="mt-3 text-sm text-red-600">{t('labels.lblScanPassportPhotoFailed')}</p>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              tabIndex={-1}
              aria-hidden
              onChange={(event) => {
                void handlePhoto(event.target.files?.[0]);
              }}
            />

            <button
              type="button"
              disabled={!canUpload}
              onClick={() => {
                void handleCapture();
              }}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {t('labels.lblScanPassportCapture')}
            </button>

            <button
              type="button"
              disabled={!canUpload}
              onClick={() => fileInputRef.current?.click()}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiUploadCloud size={16} />
              {t('labels.lblScanPassportUpload')}
            </button>

            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
              {blocked && !photoBusy && (
                <MainCustomButton
                  type="button"
                  text={t('labels.lblScanPassportRetry') || 'Try again'}
                  onClick={handleRetry}
                />
              )}
              <SecondaryCustomButton
                type="button"
                text={t('actions.close') || 'Close'}
                onClick={() => onOpenChange(false)}
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
